import { create } from 'zustand';

import { api, configureApi } from '@/lib/api';
import { getSupportAccessToken } from '@/lib/support-access';

/**
 * The church the console is working in.
 *
 * Every request that needs tenancy picks up `x-church-id` from here. The id is the
 * only thing persisted — membership, modules and entitlements are re-fetched so a
 * revoked seat or a disabled module cannot linger in local storage.
 */

export interface PublicChurch {
  id: string;
  name: string;
  campusName: string;
  slug: string;
  denomination: string | null;
  defaultLocale: string;
  defaultTimezone: string;
  baseCurrency: string;
  supportedCurrencies: string[];
  contactEmail: string | null;
  contactPhone: string | null;
  contactAlternatePhone: string | null;
  address: string | null;
  locationLatitude: number | null;
  locationLongitude: number | null;
  gpsCheckInRadiusMetres: number;
  country: string;
  status: 'active' | 'suspended' | 'banned' | 'deactivated';
  parentChurchId: string | null;
  requireTotpForPrivilegedRoles: boolean;
  isAppSearchable: boolean;
  showInNearby: boolean;
  logoKey: string | null;
  logoUrl: string | null;
  brandPrimary: string;
  brandAccent: string;
  membershipNumberMode: 'manual' | 'auto' | 'auto_with_override';
  membershipNumberPrefix: string | null;
  membershipNumberPadding: number;
  membershipNumberNextValue: number;
  booksStartDate?: string | null;
  previousRecordsUrl?: string | null;
  /** Present on current-church fetch; false when people, giving, programs, etc. are linked. */
  canDelete?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicSubscription {
  id: string;
  plan: 'trial' | 'community' | 'starter' | 'essential' | 'professional' | 'enterprise';
  status: 'trialing' | 'active' | 'past_due' | 'grace' | 'suspended' | 'cancelled' | 'expired';
  billingInterval?: 'monthly' | 'annual' | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  gracePeriodDays: number;
  includedSeats: number;
  cancelAtPeriodEnd?: boolean;
  cancelledAt: string | null;
}

export type SystemRole = 'owner' | 'admin' | 'staff' | 'congregant';

export interface ChurchMembershipSummary {
  church: PublicChurch;
  membership: {
    id: string;
    systemRole: SystemRole;
    staffRoleId: string | null;
    status: 'active' | 'invited' | 'suspended' | 'deactivated';
    joinedAt: string;
  };
  subscription: PublicSubscription;
}

export interface CurrentMembership {
  id: string;
  systemRole: SystemRole;
  staffRoleId: string | null;
  status: string;
  permissions: string[];
}

export interface EnabledModule {
  code: string;
  acquisitionType: 'included' | 'addon';
  enabledAt: string;
  enabledBy: string;
  priceMinor: number;
  priceCurrency: string;
}

export type PlanEntitlementValue = boolean | 'limited' | 'short' | 'full' | 'extended';

export interface PlanEntitlements {
  plan: PublicSubscription['plan'];
  status: string;
  features: Record<string, PlanEntitlementValue>;
}

interface ChurchState {
  status: 'unknown' | 'ready';
  /** Bumps on every church/branch switch so routed views can remount cleanly. */
  workspaceGeneration: number;
  memberships: ChurchMembershipSummary[];
  church: PublicChurch | null;
  subscription: PublicSubscription | null;
  membership: CurrentMembership | null;
  enabledModules: EnabledModule[];
  entitlements: PlanEntitlements | null;
  headOffice: { id: string; name: string } | null;
  restore: () => Promise<void>;
  selectChurch: (churchId: string) => Promise<void>;
  /** Opens the exact church a support session was granted for, including a branch. */
  openSupportSession: (churchId: string) => Promise<void>;
  switchToBranch: (branchId: string) => Promise<void>;
  setAsHeadOffice: (branchId: string) => Promise<void>;
  returnToHeadOffice: () => Promise<void>;
  refreshCurrent: () => Promise<void>;
  /** Remounts routed console views after a completed church/branch switch. */
  bumpWorkspaceGeneration: () => void;
  isModuleEnabled: (code: string) => boolean;
  allowsFeature: (feature: string) => boolean;
  reset: () => void;
}

const CHURCH_KEY = 'trinity.church.id';

const readSavedChurchId = (): string | null => {
  try {
    return localStorage.getItem(CHURCH_KEY);
  } catch {
    return null;
  }
};

/** Last church or campus opened in this browser — used as the select-page default. */
export const getSavedChurchId = (): string | null => readSavedChurchId();

const persistChurchId = (churchId: string | null): void => {
  if (getSupportAccessToken()) return;
  try {
    if (churchId) localStorage.setItem(CHURCH_KEY, churchId);
    else localStorage.removeItem(CHURCH_KEY);
  } catch {
    // Storage unavailable — the session still works for this tab.
  }
};

const emptyRuntime = {
  enabledModules: [] as EnabledModule[],
  entitlements: null as PlanEntitlements | null,
  headOffice: null as { id: string; name: string } | null,
};

const beginWorkspaceSwitch = (
  patch: Pick<ChurchState, 'church' | 'subscription' | 'headOffice'>,
): Partial<ChurchState> => ({
  membership: null,
  ...emptyRuntime,
  ...patch,
});

const congregantMembershipFromRow = (
  row: ChurchMembershipSummary,
): CurrentMembership => ({
  id: row.membership.id,
  systemRole: 'congregant',
  staffRoleId: null,
  status: row.membership.status,
  permissions: [],
});

const isCongregantRow = (row: ChurchMembershipSummary): boolean =>
  row.membership.systemRole === 'congregant';

export const useChurchStore = create<ChurchState>((set, get) => ({
  status: 'unknown',
  workspaceGeneration: 0,
  memberships: [],
  church: null,
  subscription: null,
  membership: null,
  ...emptyRuntime,

  restore: async () => {
    if (getSupportAccessToken()) {
      set({ status: 'ready' });
      return;
    }
    try {
      const { data } = await api.get<{ data: ChurchMembershipSummary[] }>('/churches', {
        withoutChurch: true,
      });

      const savedId = readSavedChurchId();
      const saved = data.find((row) => row.church.id === savedId);
      const auto = saved ?? (data.length === 1 ? data[0] : undefined);

      if (!auto) {
        persistChurchId(null);
        set({
          status: 'ready',
          memberships: data,
          church: null,
          subscription: null,
          membership: null,
          ...emptyRuntime,
        });
        return;
      }

      persistChurchId(auto.church.id);
      if (isCongregantRow(auto)) {
        set({
          status: 'ready',
          memberships: data,
          church: auto.church,
          subscription: auto.subscription,
          membership: congregantMembershipFromRow(auto),
          ...emptyRuntime,
        });
        return;
      }
      set({
        status: 'ready',
        memberships: data,
        church: auto.church,
        subscription: auto.subscription,
        membership: null,
        ...emptyRuntime,
      });

      await get().refreshCurrent();
    } catch {
      set({
        status: 'ready',
        memberships: [],
        church: null,
        subscription: null,
        membership: null,
        ...emptyRuntime,
      });
    }
  },

  openSupportSession: async (churchId) => {
    const [current, enabled, entitlements] = await Promise.all([
      api.get<{
        church: PublicChurch;
        subscription: PublicSubscription;
        membership: CurrentMembership;
      }>('/churches/current', { churchId }),
      api.get<{ data: EnabledModule[] }>('/modules/enabled', { churchId }),
      api.get<PlanEntitlements>('/plan/entitlements', { churchId }),
    ]);

    set({
      status: 'ready',
      church: current.church,
      subscription: current.subscription,
      membership: current.membership,
      enabledModules: enabled.data ?? [],
      entitlements,
      headOffice: current.church.parentChurchId ? get().headOffice : null,
    });
  },

  selectChurch: async (churchId) => {
    let row = get().memberships.find((item) => item.church.id === churchId);
    if (!row) {
      await get().restore();
      row = get().memberships.find((item) => item.church.id === churchId);
    }
    if (!row) return;

    persistChurchId(churchId);
    if (isCongregantRow(row)) {
      set({
        church: row.church,
        subscription: row.subscription,
        membership: congregantMembershipFromRow(row),
        ...emptyRuntime,
        headOffice: row.church.parentChurchId ? get().headOffice : null,
      });
      return;
    }
    set(
      beginWorkspaceSwitch({
        church: row.church,
        subscription: row.subscription,
        headOffice: row.church.parentChurchId ? get().headOffice : null,
      }),
    );
    await get().refreshCurrent();
  },

  switchToBranch: async (branchId) => {
    if (!get().church) return;

    const result = await api.post<{
      church: PublicChurch;
      subscription: PublicSubscription;
      headChurch: { id: string; name: string };
    }>(`/branches/${branchId}/switch`);

    persistChurchId(result.church.id);
    set(
      beginWorkspaceSwitch({
        church: result.church,
        subscription: result.subscription,
        headOffice: result.church.parentChurchId ? result.headChurch : null,
      }),
    );
    await get().refreshCurrent();
  },

  setAsHeadOffice: async (branchId) => {
    if (!get().church) return;

    const result = await api.post<{
      church: PublicChurch;
      subscription: PublicSubscription;
      headChurch: { id: string; name: string };
    }>(`/branches/${branchId}/set-as-head`);

    // Parent pointers moved — refresh memberships so the church picker regroups correctly.
    const { data: memberships } = await api.get<{ data: ChurchMembershipSummary[] }>(
      '/churches',
      { withoutChurch: true },
    );

    persistChurchId(result.church.id);
    set({
      memberships,
      ...beginWorkspaceSwitch({
        church: result.church,
        subscription: result.subscription,
        headOffice: null,
      }),
    });
    await get().refreshCurrent();
  },

  returnToHeadOffice: async () => {
    const headId = get().headOffice?.id ?? get().church?.parentChurchId;
    if (!headId) return;
    await get().switchToBranch(headId);
  },

  bumpWorkspaceGeneration: () => {
    set((state) => ({ workspaceGeneration: state.workspaceGeneration + 1 }));
  },

  refreshCurrent: async () => {
    if (!get().church) return;
    if (get().membership?.systemRole === 'congregant') return;

    try {
      const [current, enabled, entitlements] = await Promise.all([
        api.get<{
          church: PublicChurch;
          subscription: PublicSubscription;
          membership: CurrentMembership;
        }>('/churches/current'),
        api.get<{ data: EnabledModule[] }>('/modules/enabled'),
        api.get<PlanEntitlements>('/plan/entitlements'),
      ]);

      set({
        church: current.church,
        subscription: current.subscription,
        membership: current.membership,
        enabledModules: enabled.data ?? [],
        entitlements,
      });
    } catch {
      // Keep whatever modules we already have — a failed refresh must not blank the sidebar.
    }
  },

  isModuleEnabled: (code) => get().enabledModules.some((module) => module.code === code),

  allowsFeature: (feature) => {
    const value = get().entitlements?.features[feature];
    return value !== undefined && value !== false;
  },

  reset: () => {
    persistChurchId(null);
    set({
      status: 'unknown',
      workspaceGeneration: 0,
      memberships: [],
      church: null,
      subscription: null,
      membership: null,
      ...emptyRuntime,
    });
  },
}));

configureApi({
  churchId: () => useChurchStore.getState().church?.id ?? null,
});
